import { Injectable } from '@angular/core';
import {
    CompanyControllerService as ApiService,
    CompanyControllerXService,
    CompanyDTO,
    PlatformLicenseActivationDTO
} from '@iota/iota-management-portal-v4-client';
import { CompanyDTO as INHCoreCompanyDTO, CompanyManagementControllerService } from '@iota/inh-core-v1-management-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, forkJoin, map, mergeMap, of, take, withLatestFrom } from 'rxjs';
import {
    AddUpdateRemoveCompanyAttributes,
    CompanyActionTypes,
    LoadCompanies,
    LoadCompany,
    LoadCompanyReportingDataWithMapping,
    LoadCompanyReportingDataWithMappingFailure,
    LoadCompanyReportingDataWithMappingSuccess,
    LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoiced,
    LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoicedFailure,
    LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoicedSuccess,
    LoadReportingDataMapping,
    LoadReportingDataMappingFailure,
    LoadReportingDataMappingSuccess,
    RemoveCompanyAddress,
    RemoveCompanyAttachment,
    RemoveCompanyBankDetails,
    RemoveCompanyNote,
    RemoveCompanyVendorId,
    RemovePlatformLicenseActivationFromSelectedCompany,
    RemovePlatformLicenseActivationFromSelectedCompanyFailure,
    UpsertCompany,
    UpsertCompanyAddress,
    UpsertCompanyAttachment,
    UpsertCompanyBankDetails,
    UpsertCompanyNote,
    UpsertCompanyPlatformLicenseActivation,
    UpsertCompanyPlatformLicenseActivationFailure,
    UpsertCompanySuccess,
    UpsertCompanyVendorId,
    UpsertReportingDataMapping,
    UpsertReportingDataMappingFailure,
    UpsertReportingDataMappingSuccess
} from '../actions/company.actions';
import { LoadUserSuccess } from '../actions/user.actions';
import { selectedCompany } from '../store';
import { mapPortalAddressToInhCoreAddress } from '../entity-builder-utils';
import { toIsoTimestamp } from '../../shared/utils';

@Injectable()
export class CompanyEffects {
    loadAllCompanies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadUserSuccess, LoadCompanies),
            mergeMap(() =>
                this.companyService.getAllCompanies().pipe(
                    take(1),
                    map((companies) => ({ type: CompanyActionTypes.LoadCompaniesSuccess, companies })),
                    catchError((error) => of({ type: CompanyActionTypes.LoadCompaniesFailure, error }))
                )
            )
        )
    );

    reloadCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadCompany, UpsertCompanySuccess),
            mergeMap(({ companyId }) =>
                this.apiService.getCompany(companyId).pipe(
                    take(1),
                    map((company) => ({ type: CompanyActionTypes.LoadCompanySuccess, company })),
                    catchError((error) => of({ type: CompanyActionTypes.LoadCompanyFailure, error }))
                )
            )
        )
    );

    upsertCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertCompany),
            mergeMap(({ company }) =>
                this.apiService.upsertCompany(company, 'response').pipe(
                    take(1),
                    map((response) => ({
                        type: CompanyActionTypes.UpsertCompanySuccess,
                        company: mapDtoToCompany(response.body),
                        companyId: response.body.companyId
                    })),
                    catchError((error) => of({ type: CompanyActionTypes.UpsertCompanyFailure, error }))
                )
            )
        )
    );

    addUpdateRemoveCompanyAttributes = createEffect(() =>
        this.actions$.pipe(
            ofType(AddUpdateRemoveCompanyAttributes),
            withLatestFrom(this.store.select(selectedCompany)),
            mergeMap(([{ attributeResult }, company]) =>
                forkJoin([
                    attributeResult.upsertedAttributes?.length > 0
                        ? this.apiService.upsertCompanyAttributes(company.companyId, attributeResult.upsertedAttributes)
                        : of(null),
                    attributeResult.removedAttributeIds?.length > 0
                        ? this.apiService.removeCompanyAttributes(attributeResult.removedAttributeIds)
                        : of(null)
                ]).pipe(
                    take(1),
                    map(() => ({ type: CompanyActionTypes.LoadCompany, companyId: company.companyId })),
                    catchError((error) => of({ type: CompanyActionTypes.CompanyActionFailure, error: error }))
                )
            )
        )
    );

    upsertCompanyNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertCompanyNote),
            withLatestFrom(this.store.select(selectedCompany)),
            mergeMap(([{ note }, { companyId }]) =>
                this.apiService.upsertCompanyNote(companyId, note).pipe(
                    take(1),
                    map(() => ({
                        type: CompanyActionTypes.LoadCompany,
                        companyId
                    })),
                    catchError((error) => of({ type: CompanyActionTypes.CompanyActionFailure, error: error }))
                )
            )
        )
    );

    removeCompanyNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveCompanyNote),
            withLatestFrom(this.store.select(selectedCompany)),
            mergeMap(([{ noteId }, { companyId }]) =>
                this.apiService.removeCompanyNote(noteId).pipe(
                    take(1),
                    map(() => ({
                        type: CompanyActionTypes.LoadCompany,
                        companyId
                    })),
                    catchError((error) => of({ type: CompanyActionTypes.CompanyActionFailure, error: error }))
                )
            )
        )
    );

    upsertCompanyAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertCompanyAddress),
            withLatestFrom(this.store.select(selectedCompany)),
            mergeMap(([{ address }, { companyId }]) =>
                this.apiService.upsertCompanyAddress(companyId, address).pipe(
                    take(1),
                    map(() => ({
                        type: CompanyActionTypes.LoadCompany,
                        companyId
                    })),
                    catchError((error) => of({ type: CompanyActionTypes.CompanyActionFailure, error: error }))
                )
            )
        )
    );

    removeCompanyAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveCompanyAddress),
            withLatestFrom(this.store.select(selectedCompany)),
            mergeMap(([{ addressId }, { companyId }]) =>
                this.apiService.removeCompanyAddress(addressId).pipe(
                    take(1),
                    map(() => ({
                        type: CompanyActionTypes.LoadCompany,
                        companyId
                    })),
                    catchError((error) => of({ type: CompanyActionTypes.CompanyActionFailure, error: error }))
                )
            )
        )
    );

    upsertCompanyBankDetails$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertCompanyBankDetails),
            withLatestFrom(this.store.select(selectedCompany)),
            mergeMap(([{ bankDetails }, { companyId }]) =>
                this.apiService.upsertCompanyBankDetails(companyId, bankDetails).pipe(
                    take(1),
                    map(() => ({
                        type: CompanyActionTypes.LoadCompany,
                        companyId
                    })),
                    catchError((error) => of({ type: CompanyActionTypes.CompanyActionFailure, error: error }))
                )
            )
        )
    );

    removeCompanyBankDetails$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveCompanyBankDetails),
            withLatestFrom(this.store.select(selectedCompany)),
            mergeMap(([{ bankDetailsId }, { companyId }]) =>
                this.apiService.removeCompanyBankDetails(bankDetailsId).pipe(
                    take(1),
                    map(() => ({
                        type: CompanyActionTypes.LoadCompany,
                        companyId
                    })),
                    catchError((error) => of({ type: CompanyActionTypes.CompanyActionFailure, error: error }))
                )
            )
        )
    );

    upsertCompanyAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertCompanyAttachment),
            mergeMap(({ companyId, file, labels }) =>
                this.apiXService
                    .upsertCompanyAttachment(file, {
                        enterpriseId: companyId,
                        labels
                    })
                    .pipe(
                        take(1),
                        map(() => ({
                            type: CompanyActionTypes.LoadCompany,
                            companyId
                        })),
                        catchError((error) => of({ type: CompanyActionTypes.CompanyActionFailure, error: error }))
                    )
            )
        )
    );

    removeCompanyAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveCompanyAttachment),
            mergeMap(({ companyId, documentDescriptionId }) =>
                this.apiService.removeCompanyAttachment(documentDescriptionId).pipe(
                    take(1),
                    map(() => ({
                        type: CompanyActionTypes.LoadCompany,
                        companyId
                    })),
                    catchError((error) => of({ type: CompanyActionTypes.CompanyActionFailure, error: error }))
                )
            )
        )
    );

    upsertCompanyVendorId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertCompanyVendorId),
            withLatestFrom(this.store.select(selectedCompany)),
            mergeMap(([{ companyVendorId }, { companyId }]) =>
                this.apiService.upsertVendorId(companyId, companyVendorId).pipe(
                    take(1),
                    map(() => ({
                        type: CompanyActionTypes.LoadCompany,
                        companyId
                    })),
                    catchError((error) => of({ type: CompanyActionTypes.CompanyActionFailure, error: error }))
                )
            )
        )
    );

    removeCompanyVendorId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveCompanyVendorId),
            withLatestFrom(this.store.select(selectedCompany)),
            mergeMap(([{ companyVendorIdId }, { companyId }]) =>
                this.apiService.removeVendorId(companyVendorIdId).pipe(
                    take(1),
                    map(() => ({
                        type: CompanyActionTypes.LoadCompany,
                        companyId
                    })),
                    catchError((error) => of({ type: CompanyActionTypes.CompanyActionFailure, error: error }))
                )
            )
        )
    );

    loadReportingDataMapping$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadReportingDataMapping),
            mergeMap(({ companyId }) =>
                this.apiService.getCompanyReportingDataMapping(companyId).pipe(
                    map((data) => LoadReportingDataMappingSuccess({ data })),
                    catchError((error) => of(LoadReportingDataMappingFailure({ error })))
                )
            )
        )
    );

    upsertReportingDataMapping$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertReportingDataMapping),
            mergeMap(({ companyId, dataMapping }) =>
                this.apiService.upsertCompanyReportingDataMapping(companyId, dataMapping).pipe(
                    map(() => UpsertReportingDataMappingSuccess({ data: dataMapping })),
                    catchError((error) => of(UpsertReportingDataMappingFailure({ error })))
                )
            )
        )
    );

    loadCompanyReportingDataWithMapping$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadCompanyReportingDataWithMapping),
            mergeMap(({ companyId }) =>
                this.companyService.getReportingData(companyId).pipe(
                    map((data) => LoadCompanyReportingDataWithMappingSuccess({ data })),
                    catchError((error) => of(LoadCompanyReportingDataWithMappingFailure({ error })))
                )
            )
        )
    );

    upsertPlatformLicenseActivation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertCompanyPlatformLicenseActivation),
            withLatestFrom(this.store.select(selectedCompany)),
            mergeMap(([{ plaformLicenseActivation }, company]) => {
                const platformLicenseActivationWithCompanyId: PlatformLicenseActivationDTO = {
                    ...plaformLicenseActivation,
                    parentSkId: company?.companyId
                };

                return this.apiService.upsertPlatformLicenseActivation(company?.companyId, platformLicenseActivationWithCompanyId).pipe(
                    map(() => LoadCompany({ companyId: company.companyId })),
                    catchError((error) => of(UpsertCompanyPlatformLicenseActivationFailure({ error })))
                );
            })
        )
    );

    removePlatformLicenseActivationFromSelectedCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemovePlatformLicenseActivationFromSelectedCompany),
            withLatestFrom(this.store.select(selectedCompany)),
            mergeMap(([{ platformLicenseActivationId }, company]) => {
                return this.apiService.removePlatformLicenseActivation(company.companyId, platformLicenseActivationId).pipe(
                    map(() => LoadCompany({ companyId: company.companyId })),
                    catchError((error) => of(RemovePlatformLicenseActivationFromSelectedCompanyFailure({ error })))
                );
            })
        )
    );

    loadPlatformLicenseActivationsThatHaveNotBeenInvoiced$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoiced),
            mergeMap(({ companyId }) =>
                this.apiService.getAllPlatformLicenseActivationsThatHaveNotBeenInvoiced(companyId).pipe(
                    take(1),
                    map((licenseActivationIdsThatHaveNotBeenInvoiced) =>
                        LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoicedSuccess({
                            licenseActivationIdsThatHaveNotBeenInvoiced
                        })
                    ),
                    catchError((error) => of(LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoicedFailure({ error })))
                )
            )
        )
    );

    constructor(
        private store: Store,
        private actions$: Actions,
        private apiService: ApiService,
        private companyService: CompanyManagementControllerService,
        private apiXService: CompanyControllerXService
    ) {}
}

function mapDtoToCompany(dto: CompanyDTO): INHCoreCompanyDTO {
    return {
        id: dto.companyId,
        name: dto.name,
        number: dto.companyNumber,
        registrationNumber: dto.companyRegistrationNumber,
        naceCode: dto.naceCode,
        employeesCount: dto.companyEmployeesCount,
        centralRegulatorId: dto.centralRegulatorId,
        createdAt: toIsoTimestamp(dto.creationDateTime),
        roles: dto.roles,
        globalLocationNumber: dto.globalLocationNumber,
        addresses: dto.addresses?.map(mapPortalAddressToInhCoreAddress)
    };
}
